// SPDX-License-Identifier: AGPL-3.0-or-later

import {isInstalledIOSPwa} from '@app/features/ui/utils/PwaUtils';
import type {ResolvedVoiceProcessing} from '@app/features/voice/utils/VoiceProcessingProfile';

const DISABLED_DEEP_FILTER_NOISE_REDUCTION_LEVEL = 0;

function detectInstalledIOSPwa(): boolean {
	if (typeof window === 'undefined' || typeof document === 'undefined' || typeof navigator === 'undefined') {
		return false;
	}
	return isInstalledIOSPwa();
}

/**
 * WebKit suspends Web Audio when an installed iOS web app leaves the foreground.
 * A microphone track published from a MediaStreamAudioDestinationNode then sends
 * silence even though the underlying getUserMedia track is still live. Keep the
 * outbound microphone on the raw WebRTC capture path for these calls.
 */
export function shouldBypassVoiceInputProcessorForBackgroundCapture(
	installedIOSPwa: boolean = detectInstalledIOSPwa(),
): boolean {
	return installedIOSPwa;
}

/**
 * When DeepFilter cannot run, fall back to the browser's capture-time noise
 * suppression. Capture-time processing is applied by WebRTC and does not depend
 * on a page-owned AudioContext continuing to render in the background.
 */
export function resolveBackgroundCapableVoiceProcessing(
	profile: ResolvedVoiceProcessing,
	installedIOSPwa: boolean = detectInstalledIOSPwa(),
): ResolvedVoiceProcessing {
	if (!installedIOSPwa || !profile.deepFilter) return profile;
	return {
		...profile,
		browserNoiseSuppression: true,
		deepFilter: false,
		deepFilterNoiseReductionLevel: DISABLED_DEEP_FILTER_NOISE_REDUCTION_LEVEL,
	};
}
